import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import api from '../config/axios';
import { useAuth } from './AuthContext';
import { useUserPlan } from '../hooks/useUserPlan';

export const STRIPE_RETURN_KEY = 'gio_overdue_invoice_stripe_return';

const OverdueInvoiceContext = createContext({
  url: '',
  planName: '',
  ready: false,
  reload: async () => '',
});

let payUrl = '';

export function payOverdueInvoice(url = payUrl) {
  if (typeof url === 'string' && url.startsWith('https://')) {
    sessionStorage.setItem(STRIPE_RETURN_KEY, '1');
    window.location.assign(url);
    return true;
  }
  return false;
}

/** Fatura atrasada grava a volta. Checkout normal segue sem essa marca. */
export function openCheckoutUrl(url) {
  if (typeof url === 'string' && url.includes('invoice.stripe.com')) {
    return payOverdueInvoice(url);
  }
  if (typeof url === 'string' && url.startsWith('https://')) {
    window.location.assign(url);
    return true;
  }
  return false;
}

/** Com fatura atrasada, abre o pagamento. Sem ela, segue para a página de planos. */
export function openPlans(navigate) {
  if (payOverdueInvoice()) return;
  if (typeof navigate === 'function') navigate('/select-plan');
}

export function OverdueInvoiceProvider({ children }) {
  const { user } = useAuth();
  const { isFreeUser, userPlan } = useUserPlan();
  const [url, setUrl] = useState('');
  const [planName, setPlanName] = useState('');
  const [resolvedKey, setResolvedKey] = useState('');
  const checkGeneration = useRef(0);

  const userId = user?.id || user?.email || '';
  const subjectKey = userId && isFreeUser ? String(userId) : '';
  const planKnown = userPlan != null;
  const ready = planKnown && (subjectKey === '' || resolvedKey === subjectKey);

  const reload = useCallback(async () => {
    const generation = ++checkGeneration.current;
    if (!userId || !isFreeUser) {
      payUrl = '';
      setUrl('');
      setPlanName('');
      setResolvedKey('');
      return '';
    }
    const key = String(userId);
    setResolvedKey('');
    try {
      const res = await api.get('/api/stripe/overdue-invoice/', { timeout: 8000 });
      if (generation !== checkGeneration.current) return '';
      const next = res.data?.show && typeof res.data.url === 'string' && res.data.url.startsWith('https://')
        ? res.data.url
        : '';
      const nextPlan = next && typeof res.data?.plan === 'string' ? res.data.plan : '';
      payUrl = next;
      setUrl(next);
      setPlanName(nextPlan);
      setResolvedKey(key);
      return next;
    } catch {
      if (generation !== checkGeneration.current) return '';
      payUrl = '';
      setUrl('');
      setPlanName('');
      setResolvedKey(key);
      return '';
    }
  }, [userId, isFreeUser]);

  useEffect(() => {
    reload();
  }, [reload]);

  return (
    <OverdueInvoiceContext.Provider value={{ url, planName, ready, reload }}>
      {children}
    </OverdueInvoiceContext.Provider>
  );
}

export function useOverdueInvoice() {
  return useContext(OverdueInvoiceContext);
}

/** Com fatura atrasada, o texto do botão passa a ser o de pagar. Sem ela, fica o texto original. */
export function OverduePayText({ children }) {
  const { url, ready } = useOverdueInvoice();
  const { t } = useTranslation('header');
  if (!ready) return null;
  if (url) return t('paySubscription');
  return children;
}

/** Enquanto a checagem da fatura não termina, o bloco não aparece. */
export function OverdueKnown({ children }) {
  const { ready } = useOverdueInvoice();
  if (!ready) return null;
  return children;
}

const PAY_RED = '#d01212';
const PAY_RED_HOVER = '#e01818';

/** O aviso ao lado do botão usa a mesma decisão: pagar, ou o texto original. */
export function OverdueAside({ children, prominent = false }) {
  const { url, ready } = useOverdueInvoice();
  const { t } = useTranslation('header');
  const theme = useTheme();
  if (!ready) return null;
  if (!url) return children;
  const isDark = theme.palette.mode === 'dark';
  return (
    <Typography
      component="p"
      sx={{
        m: 0,
        mb: prominent ? 1.5 : 2.5,
        maxWidth: 560,
        mx: prominent ? 'auto' : undefined,
        fontWeight: 800,
        letterSpacing: '-0.02em',
        lineHeight: 1.35,
        fontSize: prominent
          ? { xs: '1.45rem', md: '1.85rem' }
          : { xs: '1.15rem', md: '1.35rem' },
        color: isDark ? '#ff8d8d' : PAY_RED,
      }}
    >
      {t('payNotice')}
    </Typography>
  );
}

/** Botão que paga a fatura. Com atraso, fica vermelho e com o texto de pagar. */
export function OverduePayButton({ sx, children, color, variant, ...rest }) {
  const { url, ready } = useOverdueInvoice();
  const { t } = useTranslation('header');
  if (!ready) return null;
  return (
    <Button
      {...rest}
      variant={url ? 'contained' : variant}
      color={url ? 'inherit' : color}
      sx={[
        sx,
        url && {
          bgcolor: PAY_RED,
          background: PAY_RED,
          backgroundImage: 'none',
          color: '#fff',
          boxShadow: 'none',
          '&:hover': {
            bgcolor: PAY_RED_HOVER,
            background: PAY_RED_HOVER,
            backgroundImage: 'none',
            color: '#fff',
            boxShadow: 'none',
            filter: 'none',
          },
        },
      ]}
    >
      {url ? t('paySubscription') : children}
    </Button>
  );
}

/** Alerta ao lado do botão. Com fatura atrasada, o fundo e o ícone ficam vermelhos. */
export function OverdueAlert({ severity = 'info', sx, children, ...rest }) {
  const { url, ready } = useOverdueInvoice();
  const theme = useTheme();
  if (!ready) return null;
  const isDark = theme.palette.mode === 'dark';
  return (
    <Alert
      {...rest}
      severity={url ? 'error' : severity}
      sx={[
        sx,
        url && {
          bgcolor: isDark ? 'rgba(208, 18, 18, 0.18)' : 'rgba(208, 18, 18, 0.08)',
          color: isDark ? '#ffd6d6' : '#6d1212',
          alignItems: 'flex-start',
          '& .MuiAlert-icon': { color: isDark ? '#ff8d8d' : PAY_RED },
        },
      ]}
    >
      {children}
    </Alert>
  );
}
